import { getToken } from "./supabase";
import type { CoverSavePayload } from "@/components/banner/CoverEditor";
import type { ArtistsResponse } from "@/types";
import type { ArtistProfile } from "@/types/artist";
import type { PortfolioImage } from "@/types/portfolio";

const API_BASE = "/api";

// ============================================================================
// Public API (No Auth Required)
// ============================================================================

interface GetArtistsParams {
  status?: "open" | "closed" | "waitlist";
  tags?: string[];
  slug?: string;
  contentRating?: string;
}

export async function getArtists(params: GetArtistsParams = {}): Promise<ArtistsResponse> {
  const searchParams = new URLSearchParams();

  if (params.status) {
    searchParams.set("status", params.status);
  }
  if (params.tags && params.tags.length > 0) {
    searchParams.set("tags", params.tags.join(","));
  }
  if (params.slug) {
    searchParams.set("slug", params.slug);
  }
  if (params.contentRating) {
    searchParams.set("contentRating", params.contentRating);
  }

  const url = `${API_BASE}/get-artists${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch artists: ${response.statusText}`);
  }

  return response.json();
}

export async function getArtistBySlug(slug: string, contentRating?: string): Promise<ArtistsResponse> {
  return getArtists({ slug, contentRating });
}

// ============================================================================
// Authenticated API (Dashboard)
// ============================================================================

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Request failed");
  }

  return data;
}

// Artist Profile
export async function getArtistProfile(): Promise<ArtistProfile> {
  const data = await fetchWithAuth<{ artist: ArtistProfile }>("/artist/profile");
  return data.artist;
}

export async function updateArtistProfile(
  updates: Partial<ArtistProfile>
): Promise<ArtistProfile> {
  const data = await fetchWithAuth<{ artist: ArtistProfile }>("/artist/profile", {
    method: "PUT",
    body: JSON.stringify(updates),
  });
  return data.artist;
}

// Portfolio
export async function getPortfolioImages(): Promise<PortfolioImage[]> {
  const data = await fetchWithAuth<{ images: PortfolioImage[] }>("/portfolio");
  return data.images || [];
}

export async function uploadPortfolioImage(
  file: File,
  tags: string[] = []
): Promise<PortfolioImage> {
  const token = getToken();
  if (!token) throw new Error("Not authenticated");

  const formData = new FormData();
  formData.append("image", file);
  formData.append("tags", JSON.stringify(tags));

  const response = await fetch(`${API_BASE}/portfolio/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Upload failed");
  return data.image;
}

export async function deletePortfolioImage(imageId: string): Promise<void> {
  await fetchWithAuth("/portfolio/delete", {
    method: "DELETE",
    body: JSON.stringify({ imageId }),
  });
}

export async function reorderPortfolioImages(
  positions: Array<{ id: string; display_order: number }>
): Promise<PortfolioImage[]> {
  const data = await fetchWithAuth<{ images: PortfolioImage[] }>("/portfolio/reorder", {
    method: "PUT",
    body: JSON.stringify({ order: positions }),
  });
  return data.images;
}

export async function updatePortfolioImage(
  imageId: string,
  updates: { tags?: string[]; content_rating?: string; commissioner_name?: string; alt_text?: string }
): Promise<PortfolioImage> {
  const data = await fetchWithAuth<{ image: PortfolioImage }>("/portfolio/update", {
    method: "PUT",
    body: JSON.stringify({ imageId, ...updates }),
  });
  return data.image;
}

// Cover Image — upload a new original, switch to a portfolio piece, or
// re-frame. See netlify/functions/lib/cover.js.
export async function saveCover(payload: CoverSavePayload): Promise<{ artist: Partial<ArtistProfile> }> {
  const token = getToken();
  if (!token) throw new Error("Not authenticated");

  let body: BodyInit;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (payload.kind === "file") {
    const formData = new FormData();
    formData.append("image", payload.file, payload.file.name);
    if (payload.framing) {
      formData.append("focalX", String(payload.framing.x));
      formData.append("focalY", String(payload.framing.y));
      formData.append("zoom", String(payload.framing.zoom));
    }
    body = formData;
  } else {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify({
      framing: payload.framing,
      portfolioImageId: payload.kind === "portfolio" ? payload.portfolioImageId : undefined,
    });
  }

  const response = await fetch(`${API_BASE}/cover/upload`, { method: "POST", headers, body });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to save banner");
  return data;
}

// Profile Picture
export async function uploadProfilePicture(file: File): Promise<ArtistProfile> {
  const token = getToken();
  if (!token) throw new Error("Not authenticated");

  const formData = new FormData();
  formData.append("image", file);

  const response = await fetch(`${API_BASE}/profile-picture/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Upload failed");
  return data.artist;
}

// Tag Suggestions
export async function getTagSuggestions(
  search: string,
  category: string
): Promise<Array<{ tag: string; category: string }>> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (category) params.set("category", category);

  const response = await fetch(`/.netlify/functions/tag-suggestions?${params}`);
  const data = await response.json();
  return data.success ? data.suggestions : [];
}

// Alt Text Generation
export async function generateAltText(imageUrl: string): Promise<string> {
  const data = await fetchWithAuth<{ alt_text: string }>("/generate-alt-text", {
    method: "POST",
    body: JSON.stringify({ image_url: imageUrl }),
  });
  return data.alt_text;
}

// Email Update
export async function updateEmail(newEmail: string): Promise<void> {
  await fetchWithAuth("/artist/update-email", {
    method: "POST",
    body: JSON.stringify({ newEmail }),
  });
}
